/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Building2Icon,
  DollarSign,
  ReceiptText,
  Users,
  Wallet,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  Rectangle,
  // RadialBar,
  // RadialBarChart,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarDatePicker } from "../../components/CalendarDatePicker";
import moment from "moment";
import { API, defaultLimitPagination, fileUpload, get } from "../../config";
import MainBody from "../../components/MainBody/MainBody";
import { formatNumber, getModuleNamingBySlug } from "../../lib/Misc";
import { useTrust } from "../../lib/context/TrustContext";
import WorkProgress from "../../WorkProgress";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../components/use-media-query";
import { MonthRangePicker } from "../../components/ui/monthrangepicker";
import NumberFlow from "@number-flow/react";
import { useOrganization } from "../../lib/context/OrganisationContext";

const COLORS = [
  "#1F78B4",
  "#1B9BDF",
  "#034262",
  "#C7DAE3",
  "#A6CEE3",
  "#B2DF8A",
  "#d3f7b2",
  "#2b6a8a",
  "#6a9aba",
  "#0f71a3",
];

const TrustDashboard = (props) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { organizationData } = useOrganization();

  const navigate = useNavigate();
  const [chartData, setChartData] = useState();
  // const [chartData, setCardData] = useState();
  // const trustId = "829c6387-078b-436d-a894-e70e83344a42";
  const { trustId } = useTrust();

  const [loading, setLoading] = useState();
  // const [selectedDateRange, setSelectedDateRange] = useState({
  //   from: new Date(new Date().getFullYear(), 0, 1),
  //   to: new Date(),
  // });

  const [selectedMonthRange, setSelectedMonthRange] = React.useState({
    start: new Date(new Date().getFullYear(), 0, 1),
    end: new Date(),
  });

  // const fetchDashboardDetails = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     const { data } = await get(
  //       `${API.GET_TRUST_DASHBOARD}?trust_id=${trustId}`
  //     );
  //     setCardData(data?.data);
  //     setLoading(false);
  //   } catch (error) {
  //     setLoading(false);
  //   }
  // }, [trustId]);

  // useEffect(() => {
  //   fetchDashboardDetails();
  // }, [fetchDashboardDetails]);

  const fetchGraphData = useCallback(async () => {
    if (trustId) {
      try {
        setLoading(true);
        const { data } = await fileUpload(
          `${API.GET_TRUST_DASHBOARD_GRAPH}?trust_id=${
            trustId ? trustId : ""
          }&from_month=${
            moment(selectedMonthRange?.start).format("MM-YYYY")
              ? moment(selectedMonthRange?.start).format("MM-YYYY")
              : ""
          }&to_month=${
            moment(selectedMonthRange?.end).format("MM-YYYY")
              ? moment(selectedMonthRange?.end).format("MM-YYYY")
              : ""
          }`
          // formData
        );
        setLoading(false);

        setChartData(data?.data);

        return data.data;
      } catch (e) {
        setLoading(false);
      }
    }
  }, [selectedMonthRange?.start, selectedMonthRange?.end, trustId]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  const chartConfig = {
    reconciled: {
      label: "Reconciled",
      color: "hsl(var(--chart-3))",
    },
    unreconciled: {
      label: "Not Reconciled",
      color: "hsl(var(--chart-4))",
    },
  };

  const paymentChartConfig = {
    payments: {
      label: "Payment",
      color: "hsl(var(--chart-4))",
    },
    receipts: {
      label: "Receipt",
      color: "hsl(var(--chart-3))",
    },
  };

  var pieChartConfig = {};

  for (let i = 0; i < chartData?.vendorChart?.length; i++) {
    pieChartConfig[`${chartData?.vendorChart[i]?.name}`] = {
      label: chartData?.vendorChart[i]?.name,
    };
  }

  const usedValue = chartData?.proCostChart?.total;
  const estimatedValue = chartData?.proCostChart?.estimated_cost;

  const isOverBudget = usedValue > estimatedValue;
  const radialData = [
    {
      label: "Estimated Value",
      name: "Estimated Value",
      value: estimatedValue,
      fill: "hsl(var(--chart-1))", // Default color for estimated value
    },
    {
      label: "Used Value",
      name: "Used Value",
      value: usedValue,
      fill: isOverBudget ? "#ff6b6b" : "#82ca9d", // Red if over budget, green if under
    },
  ];

  var radialChartConfig = {};

  for (let i = 0; i < radialData?.length; i++) {
    radialChartConfig[`${radialData[i]?.name}`] = {
      label: radialData[i]?.name,
    };
  }

  const navigateBar = (data, type) => {
    const input = data?.payload?.month;
    if (input) {
      // Parse the date
      const parsedDate = new Date(input);

      // Get year and month
      const year = parsedDate.getFullYear();
      const month = parsedDate.getMonth(); // 0-based (June is 5)

      // First day of the month
      const fromDate = new Date(year, month, 1);

      // Last day of the month
      const toDate = new Date(year, month + 1, 0);

      // Format as dd-mm-yyyy
      const formatDate = (date) =>
        `${String(date.getDate()).padStart(2, "0")}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${date.getFullYear()}`;

      const from = formatDate(fromDate);
      const to = formatDate(toDate);

      let url =
        type === "reconciled"
          ? `/trusts/${trustId}/banking?tab=reconcile-item&limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
          : type === "unreconciled"
          ? `/trusts/${trustId}/banking?tab=reconcile-transactions&view=grid&from=${from}&to=${to}`
          : type === "receipts"
          ? `/trusts/${trustId}/receipts?limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
          : type === "payments"
          ? `/trusts/${trustId}/payments?limit=${defaultLimitPagination}&page=1&from=${from}&to=${to}`
          : `/trusts/${trustId}/dashboard`;

      navigate(url);
    }
    if (type === "vendorPayment") {
      const vendorId = data?.id;
      navigate(`/trusts/${trustId}/payments`, {
        state: {
          vendorId: vendorId,
        },
      });
    }
  };

  useEffect(() => {
    navigate(
      `/trusts/${trustId}/dashboard?from=${
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      }&to=${
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      }`,
      { replace: true }
    );
  }, [navigate, selectedMonthRange?.end, selectedMonthRange?.start, trustId]);

  return (
    <MainBody
      search={false}
      actionRight={true}
      actionContent={
        <>
          <div className="flex items-center gap-2 -mt-1 flex-wrap">
            <Button onClick={() => navigate(`/trusts/${trustId}/receipts`)}>
              Enter Trust Receipt
            </Button>
            <Button onClick={() => navigate(`/trusts/${trustId}/payments`)}>
              Enter Trust Payment
            </Button>
          </div>
          {/* <CalendarDatePicker
            date={selectedDateRange}
            onDateSelect={setSelectedDateRange}
            variant="outline"
            align={isDesktop ? "end" : "start"}
            disabledDate={(date) => date > new Date()}
          /> */}
          <MonthRangePicker
            onMonthRangeSelect={setSelectedMonthRange}
            selectedMonthRange={selectedMonthRange}
            variant="outline"
            align={isDesktop ? "end" : "end"}
            maxDate={new Date()}
          />
        </>
      }
      mainClassName="w-[calc(100%-3rem)]"
      loading={loading}
    >
      <div className="flex w-full flex-col">
        <main className="flex flex-1 flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <Card
              className="group transition-all hover:bg-accent hover:cursor-pointer"
              onClick={() => {
                navigate(`/trusts/${trustId}/contacts`);
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "contacts",
                    "Customer"
                  )}
                </CardTitle>
                <Users className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.customers ? chartData?.customers : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "contacts",
                    "Customer"
                  )}
                </p>
              </CardContent>
            </Card>
            <Card
              className="group transition-all hover:bg-accent hover:cursor-pointer"
              onClick={() => {
                navigate(`/trusts/${trustId}/vendors`);
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "vendors",
                    "Vendor"
                  )}
                </CardTitle>
                <Users className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.vendors ? chartData?.vendors : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "vendors",
                    "Vendor"
                  )}
                </p>
              </CardContent>
            </Card>
            {/* <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  Assets
                </CardTitle>
                <Building2Icon className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.assets ? chartData?.assets : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Assets</p>
              </CardContent>
            </Card> */}
            <Card
              className="group transition-all hover:bg-accent hover:cursor-pointer"
              onClick={() => {
                navigate(`/trusts/${trustId}/sub-assets`);
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "sub-assets",
                    "Sub Asset"
                  )}
                </CardTitle>
                <Building2Icon className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.subAssets ? chartData?.subAssets : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "sub-assets",
                    "Sub Asset"
                  )}
                </p>
              </CardContent>
            </Card>
            <Card
              className="group transition-all hover:bg-accent hover:cursor-pointer"
              onClick={() => {
                navigate(`/trusts/${trustId}/payments`);
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  Payments
                </CardTitle>
                <Wallet className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.payments ? chartData?.payments : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Payments</p>
              </CardContent>
            </Card>
            <Card
              className="group transition-all hover:bg-accent hover:cursor-pointer"
              onClick={() => {
                navigate(`/trusts/${trustId}/receipts`);
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  Receipts
                </CardTitle>
                <ReceiptText className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-xl font-bold">
                  <NumberFlow
                    value={chartData?.receipts ? chartData?.receipts : 0}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Receipts</p>
              </CardContent>
            </Card>
            {/* <Card className="group transition-all hover:bg-accent hover:cursor-pointer xl:col-span-3">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  Trust Cash Flow Forecast
                </CardTitle>
                <DollarSign className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-row gap-5 flex-wrap">
                  <div>
                    <div className="text-xl font-bold">
                      {chartData?.cashFlow?.current
                        ? chartData?.cashFlow?.current < 0
                          ? `($${formatNumber(
                              Number(chartData?.cashFlow?.current) * -1
                            )})`
                          : `$${formatNumber(
                              Number(chartData?.cashFlow?.current)
                            )}`
                        : "$0.00"}
                    </div>
                    <p className="text-xs text-muted-foreground">Currrent</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      {chartData?.cashFlow?.balance_after_payment
                        ? chartData?.cashFlow?.balance_after_payment < 0
                          ? `($${formatNumber(
                              Number(
                                chartData?.cashFlow?.balance_after_payment
                              ) * -1
                            )})`
                          : `$${formatNumber(
                              Number(chartData?.cashFlow?.balance_after_payment)
                            )}`
                        : "$0.00"}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Balance After Payment
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card> */}
            <Card className="group transition-all hover:bg-accent hover:cursor-pointer xl:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm font-medium group-hover:bg-webPrimary-foreground">
                  Trust Bank Balance
                </CardTitle>
                <DollarSign className="h-6 w-6 text-muted-foreground group-hover:text-webPrimary" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex flex-row xl:gap-16 gap-4 flex-wrap">
                  <div>
                    <div className="text-xl font-bold">
                      {chartData?.trustBalance?.statement
                        ? chartData?.trustBalance?.statement < 0
                          ? `($${formatNumber(
                              Number(chartData?.trustBalance?.statement) * -1
                            )})`
                          : `$${formatNumber(
                              Number(chartData?.trustBalance?.statement)
                            )}`
                        : "$0.00"}
                    </div>
                    <p className="text-xs text-muted-foreground">Statement</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold">
                      {chartData?.trustBalance?.trustSoft
                        ? chartData?.trustBalance?.trustSoft < 0
                          ? `($${formatNumber(
                              Number(chartData?.trustBalance?.trustSoft) * -1
                            )})`
                          : `$${formatNumber(
                              Number(chartData?.trustBalance?.trustSoft)
                            )}`
                        : "$0.00"}
                    </div>
                    <p className="text-xs text-muted-foreground">TrustSoft</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
              <CardHeader className="p-4 pb-6 text-center items-center">
                <CardTitle className="group-hover:bg-webPrimary-foreground">
                  Trust Bank Reconcilation
                </CardTitle>
                <CardDescription>
                  {moment(selectedMonthRange?.start)?.format("MMM-YYYY") ===
                  moment(selectedMonthRange?.end)?.format("MMM-YYYY")
                    ? moment(selectedMonthRange?.start)?.format("MMM-YYYY")
                    : `${moment(selectedMonthRange?.start)?.format(
                        "MMM-YYYY"
                      )} ~ ${moment(selectedMonthRange?.end)?.format(
                        "MMM-YYYY"
                      )}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ChartContainer config={chartConfig}>
                  <BarChart
                    accessibilityLayer
                    data={chartData?.transactionsChart}
                  >
                    <CartesianGrid
                      vertical={false}
                      className="!stroke-border group-hover:dark:!stroke-muted-foreground"
                    />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      tickFormatter={(value) => value.slice(0, 3)}
                    />
                    <YAxis allowDecimals={false} />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          hideLabel
                          className="w-[180px]"
                          formatter={(value, name, item, index) => (
                            <>
                              {index === 0 && (
                                <div className="mb-1 flex basis-full items-center text-xs font-bold text-foreground">
                                  {item.payload.month}
                                </div>
                              )}
                              <div
                                className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                                style={{
                                  "--color-bg": `var(--color-${name})`,
                                }}
                              />
                              {chartConfig[name]?.label || name}
                              <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                {Number(value)}
                              </div>
                              {index === 1 && (
                                <div className="mt-1.5 flex basis-full items-center border-t pt-1.5 text-xs font-medium text-foreground">
                                  Total
                                  <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                    {Number(
                                      item.payload.reconciled +
                                        item.payload.unreconciled
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        />
                      }
                      cursor={false}
                    />
                    <Bar
                      dataKey="reconciled"
                      fill="var(--color-reconciled)"
                      // radius={4}
                      stackId="a"
                      // radius={[0, 0, 0, 0]}
                      shape={(props) => {
                        const { payload, ...rest } = props;
                        const radius =
                          payload.unreconciled === 0
                            ? [4, 4, 0, 0]
                            : [0, 0, 0, 0];
                        return <Rectangle {...rest} radius={radius} />;
                      }}
                      className="hover:cursor-pointer"
                      onClick={(data, index) => {
                        navigateBar(data, "reconciled");
                      }}
                    />
                    <Bar
                      dataKey="unreconciled"
                      fill="var(--color-unreconciled)"
                      // radius={4}
                      stackId="a"
                      radius={[4, 4, 0, 0]}
                      className="hover:cursor-pointer"
                      onClick={(data, index) => {
                        navigateBar(data, "unreconciled");
                      }}
                    />
                    <ChartLegend
                      content={<ChartLegendContent />}
                      className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                    />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
            <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
              <CardHeader className="items-center p-4">
                <CardTitle className="group-hover:bg-webPrimary-foreground">
                  Payment to{" "}
                  {getModuleNamingBySlug(
                    organizationData?.organizationData,
                    "vendors",
                    "Vendor"
                  )}
                </CardTitle>
                <CardDescription>
                  {moment(selectedMonthRange?.start)?.format("MMM-YYYY") ===
                  moment(selectedMonthRange?.end)?.format("MMM-YYYY")
                    ? moment(selectedMonthRange?.start)?.format("MMM-YYYY")
                    : `${moment(selectedMonthRange?.start)?.format(
                        "MMM-YYYY"
                      )} ~ ${moment(selectedMonthRange?.end)?.format(
                        "MMM-YYYY"
                      )}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 p-4 pt-0">
                <ChartContainer
                  config={pieChartConfig}
                  className="mx-auto max-h-[400px]"
                >
                  <PieChart>
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          nameKey="name"
                          hideLabel
                          formatter={(value, name, item, index) => (
                            <>
                              <div
                                className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[var(--color-bg)]"
                                style={{
                                  "--color-bg": `${
                                    COLORS[
                                      chartData?.vendorsChart?.findIndex(
                                        (item) =>
                                          item.value === value &&
                                          item.name === name
                                      ) % COLORS.length
                                    ]
                                  }`,
                                }}
                              />
                              {chartConfig[name]?.label || name}
                              <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                {formatNumber(
                                  Number(item?.payload?.payload?.amount)
                                )}{" "}
                                ({`${value}%`})
                              </div>
                            </>
                          )}
                        />
                      }
                    />
                    <Pie
                      data={chartData?.vendorsChart}
                      dataKey="value"
                      label={({ payload, ...props }) => {
                        return (
                          <text
                            cx={props.cx}
                            cy={props.cy}
                            x={props.x}
                            y={props.y}
                            textAnchor={props.textAnchor}
                            dominantBaseline={props.dominantBaseline}
                            fill={COLORS[props?.index % COLORS.length]}
                          >
                            {`${payload.value}`}
                          </text>
                        );
                      }}
                    >
                      {chartData?.vendorsChart?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          onClick={(data) => {
                            navigateBar(entry, "vendorPayment");
                          }}
                        />
                      ))}
                    </Pie>
                    <ChartLegend
                      content={<ChartLegendContent nameKey="name" />}
                      className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center flex-col items-start inline-flex max-w-44"
                      verticalAlign="bottom"
                      align="right"
                      layout="vertical"
                    />
                  </PieChart>
                </ChartContainer>
              </CardContent>
            </Card>
            <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
              <CardHeader className="p-4 pb-6 text-center items-center">
                <CardTitle className="group-hover:bg-webPrimary-foreground">
                  Payment and Receipts
                </CardTitle>
                <CardDescription>
                  {moment(selectedMonthRange?.start)?.format("MMM-YYYY") ===
                  moment(selectedMonthRange?.end)?.format("MMM-YYYY")
                    ? moment(selectedMonthRange?.start)?.format("MMM-YYYY")
                    : `${moment(selectedMonthRange?.start)?.format(
                        "MMM-YYYY"
                      )} ~ ${moment(selectedMonthRange?.end)?.format(
                        "MMM-YYYY"
                      )}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <ChartContainer config={paymentChartConfig}>
                  <BarChart
                    accessibilityLayer
                    data={chartData?.paymentsReceiptsChart}
                  >
                    <CartesianGrid
                      vertical={false}
                      className="!stroke-border group-hover:dark:!stroke-muted-foreground"
                    />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      tickFormatter={(value) => value.slice(0, 3)}
                    />
                    <YAxis />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          hideLabel
                          className="w-[180px]"
                          formatter={(value, name, item, index) => (
                            <>
                              {index === 0 && (
                                <div className="mb-1 flex basis-full items-center text-xs font-bold text-foreground">
                                  {item.payload.month}
                                </div>
                              )}
                              <div
                                className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                                style={{
                                  "--color-bg": `var(--color-${name})`,
                                }}
                              />
                              {paymentChartConfig[name]?.label || name}
                              <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                {formatNumber(Number(value))}
                              </div>
                              {index === 1 && (
                                <div className="mt-1.5 flex basis-full items-center border-t pt-1.5 text-xs font-medium text-foreground">
                                  Balance
                                  <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                                    {Number(
                                      item.payload.receipts -
                                        item.payload.payments
                                    ) < 0 ? (
                                      <span className="text-red-500">{`($${formatNumber(
                                        Math.abs(
                                          Number(
                                            item.payload.receipts -
                                              item.payload.payments
                                          )
                                        )
                                      )})`}</span>
                                    ) : (
                                      <span className="text-green-500">{`$${formatNumber(
                                        Math.abs(
                                          Number(
                                            item.payload.receipts -
                                              item.payload.payments
                                          )
                                        )
                                      )}`}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        />
                      }
                      cursor={false}
                    />
                    <Bar
                      dataKey="payments"
                      fill="var(--color-payments)"
                      radius={[4, 4, 0, 0]}
                      className="hover:cursor-pointer"
                      onClick={(data, index) => {
                        navigateBar(data, "payments");
                      }}
                    />
                    <Bar
                      dataKey="receipts"
                      fill="var(--color-receipts)"
                      radius={[4, 4, 0, 0]}
                      className="hover:cursor-pointer"
                      onClick={(data, index) => {
                        navigateBar(data, "receipts");
                      }}
                    />
                    <ChartLegend
                      content={<ChartLegendContent />}
                      className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                    />
                  </BarChart>
                </ChartContainer>
              </CardContent>
            </Card>
            {/* <Card className="group transition-all hover:bg-accent hover:cursor-pointer">
              <CardHeader className="items-center p-4">
                <CardTitle className="group-hover:bg-webPrimary-foreground">
                  Project Budget
                </CardTitle>
                <CardDescription>
                  <div
                    className={`text-center ${
                      isOverBudget ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {isOverBudget ? "Over Budget" : "Under Budget"}
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 p-4 pt-0">
                <ChartContainer
                  config={radialChartConfig}
                  className="mx-auto max-h-[400px]"
                >
                  <RadialBarChart
                    innerRadius="10%"
                    outerRadius="80%"
                    data={radialData}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent nameKey="label" hideLabel />
                      }
                    />
                    <RadialBar dataKey="value" />
                    <ChartLegend
                      content={<ChartLegendContent nameKey="name" />}
                      className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
                    />
                  </RadialBarChart>
                </ChartContainer>
              </CardContent>
            </Card> */}
          </div>
        </main>
      </div>
      {/* <WorkProgress expandList={true} /> */}
    </MainBody>
  );
};

export default TrustDashboard;
